import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
  const [token, setToken] = useState(() => localStorage.getItem("caneat_token") || null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const { data } = await api.get("/auth/me", { headers });
        if (active) setUser(data);
      } catch {
        if (active) setUser(false);
      }
    })();
    return () => { active = false; };
  }, [token]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) {
        localStorage.setItem("caneat_token", data.token);
        setToken(data.token);
      }
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("caneat_token");
    setToken(null);
    setUser(false);
  };

  const authHeaders = () => (token ? { Authorization: `Bearer ${token}` } : {});

  const changePassword = async (current_password, new_password) => {
    try {
      await api.post("/auth/change-password", { current_password, new_password }, { headers: authHeaders() });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const role = user && user.role ? user.role : null;
  const canEdit = role === "super_admin" || role === "editor";
  const isSuper = role === "super_admin";

  return (
    <AuthContext.Provider value={{ user, login, logout, authHeaders, token, changePassword, role, canEdit, isSuper }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
